// TODO-tests
import { ReactNode } from 'react';
import { IntlFormatters, IntlShape } from 'react-intl';
import type {
    EventItemsSelectionItem,
    Event,
    MembershipPurchaseInfo,
    MembershipPurchaseCountItem,
    PurchaseInfoItem,
    EventBookingPurchaseInfo,
    EventPurchaseCourseBooksInfo,
    EventBookingTicket
} from '~/shared/utils/booking-utils/types';
import {
    getTicketPrice,
    getSelectedItemsAccreditationPoints
} from '~/shared/utils/booking-utils/common.utils';
import {
    booleanFilter,
    getMembershipCreditItem,
    getMembershipCreditNameForItem
} from '~/shared/utils';
import { Fragments, Types, Queries } from '~/graphql';
import { type UserMembership } from '~/core/types/types';
import { EventOptions } from '~/shared/formOptions';
import {
    formatTranslation,
    formatDate,
    formatEnum,
    formatPrice,
    formatDateRange
} from '~/shared/formatters';
import { DateFormat, parseDBDate } from 'components';
import { getIsSyfatCourse } from '~/containers/OnDemandCourse/course.utils';
import { isPercentagePromoCode } from '~/shared/utils/booking';

const getPrintedCourseBookTicket = (selectedItems: Array<EventItemsSelectionItem> | undefined) => {
    const printedCourseBookTicket = (selectedItems || []).find(
        (item) => item.ticketType === Types.TicketType.PrintedCourseBook
    );
    return printedCourseBookTicket;
};

export const getMembershipPurchaseInfo = ({
    event,
    membershipToOrder,
    topUpToOrder,
    totalPrice,
    formatMessage,
    locale,
    userMembership,
    selectedItems,
    promoCodeApplied,
    currencySymbol,
    hideDigitalCoursebook,
    content
}: {
    event: Event | null;
    membershipToOrder: Fragments.MembershipFieldsFragment | null | undefined;
    topUpToOrder: Types.MembershipItem | null | undefined;
    totalPrice: number;
    formatMessage: IntlShape['formatMessage'];
    locale: Locale;
    currencySymbol: string;
    userMembership?: UserMembership | null | undefined;
    selectedItems?: Array<EventItemsSelectionItem>;
    promoCodeApplied?: Fragments.PromoCodeForOrderFieldsFragment | null;
    hideDigitalCoursebook: boolean;
    content?: Pick<Queries.GetContentByContentIdQuery['content'], 'brand' | 'contentId'>;
}): MembershipPurchaseInfo | null => {
    if (membershipToOrder && userMembership) {
        throw new Error(
            'One and only one of "membershipToOrder" and "userMembership" should be provided'
        );
    }

    if (membershipToOrder && topUpToOrder) {
        throw new Error(
            'One and only one of "membershipToOrder" and "topUpToOrder" should be provided'
        );
    }

    const membership = membershipToOrder || userMembership?.membership;

    if (!membership) {
        return null;
    }
    const membershipCreditItem = getMembershipCreditItem(membership.membershipItems || []);
    const creditName =
        (membershipCreditItem && getMembershipCreditNameForItem(membershipCreditItem, locale)) ||
        '';

    let accreditationPoints: MembershipPurchaseCountItem = {
        currentlyAvailable: 0,
        availableAfterBooking: 0,
        isSufficient: false,
        creditName
    };

    const userHasActiveMembership = !!userMembership;

    if (selectedItems && userMembership) {
        const membershipCreditAvailable = userMembership.accreditationPoints.remaining;

        const requiredAccreditationPoints = getSelectedItemsAccreditationPoints(
            selectedItems,
            'MEMBERSHIP_DEDUCTION'
        );

        const accreditationPointsLeft = membershipCreditAvailable - requiredAccreditationPoints;

        accreditationPoints = {
            currentlyAvailable: membershipCreditAvailable,
            availableAfterBooking: accreditationPointsLeft,
            isSufficient: accreditationPointsLeft >= 0,
            creditName
        };
    }

    if (membershipToOrder) {
        const membershipCreditAvailable =
            getMembershipCreditItem(membershipToOrder.membershipItems || [])?.amount || 0;

        accreditationPoints = {
            currentlyAvailable: membershipCreditAvailable,
            availableAfterBooking: null,
            isSufficient: true,
            creditName
        };
    }

    if (topUpToOrder && userMembership) {
        const availableCredits = userMembership.accreditationPoints.remaining;
        const addedCredits = topUpToOrder.amount;

        if (typeof addedCredits !== 'number') {
            throw new Error('TopUp amount must be present');
        }

        accreditationPoints = {
            currentlyAvailable: availableCredits,
            availableAfterBooking: availableCredits + addedCredits,
            isSufficient: true,
            creditName
        };
    }

    const isSyfatCourse = getIsSyfatCourse(content?.brand);

    let tickets: Array<{
        name: string;
        items: Array<ReactNode>;
        price: number | null;
        priceWithDiscount: number | null;
    }>;

    if (topUpToOrder) {
        const price = topUpToOrder.price;

        if (typeof price !== 'number') {
            throw new Error('TopUp must have a price');
        }

        tickets = [
            {
                name: formatTranslation(topUpToOrder.name, { locale }) || '',
                items: [],
                price,
                priceWithDiscount: null
            }
        ];
    } else {
        tickets = (selectedItems || [])
            .filter((ticket) => ticket.ticketType !== Types.TicketType.PrintedCourseBook)
            .map((ticket) => {
                let name: string;
                let items: Array<ReactNode>;
                if (ticket.ticketType === Types.TicketType.EntireEvent) {
                    name = getEntireEventTicketLabel(ticket, formatMessage);
                    const startDate = parseDBDate(ticket.startDateTime);
                    const endDate = parseDBDate(ticket.endDateTime);
                    const daysCount = endDate.diff(startDate, 'days') + 1;

                    items = new Array(daysCount).fill(null).map((item, index) => {
                        const dayLabel = formatDate(
                            startDate.add(index, 'days'),
                            DateFormat.dayAndMonthSingleLine
                        );

                        const participationTypeLabel = formatEnum(ticket.participationType, {
                            options: EventOptions.participationType,
                            formatMessage
                        });

                        return `${dayLabel} - ${participationTypeLabel}`;
                    });
                } else {
                    name = ticket.name;
                    items = [];
                }

                return {
                    name,
                    items,
                    price: ticket.price,
                    priceWithDiscount: ticket.priceWithDiscount
                };
            });
    }

    if (isSyfatCourse) {
        tickets.push({
            items: [],
            name: 'Facharztvorbereitung Allgemeinmedizin',
            price: null,
            priceWithDiscount: null
        });
    }
    const promoCodeInfo = promoCodeApplied
        ? getPromoCodeItem(promoCodeApplied, currencySymbol, locale)
        : null;

    const printedCourseBookTicket = getPrintedCourseBookTicket(selectedItems);

    const courseBooksInfo = {
        printed: printedCourseBookTicket
            ? {
                  name: formatMessage({ id: 'common.printed-coursebook' }),
                  price: getTicketPrice(printedCourseBookTicket),
                  priceWithDiscount: null,
                  items: []
              }
            : undefined,
        digital:
            hideDigitalCoursebook || !!event?.existingBookingId
                ? undefined
                : {
                      name: formatMessage({ id: 'common.digital-coursebook' }),
                      price: 0,
                      priceWithDiscount: null,
                      items: []
                  }
    };

    return {
        event,
        membership,
        userHasActiveMembership,
        accreditationPoints,
        tickets,
        totalPrice,
        promoCodeInfo,
        courseBooksInfo,
        topUp: topUpToOrder,
        promoCodeApplied,
        userMembership,
        content
    };
};

const getEventPurchaseItems = (
    selectedItem: EventItemsSelectionItem,
    formatMessage: IntlFormatters['formatMessage'],
    currencySymbol: string,
    showIsItemAddedLabel: boolean,
    locale: Locale
): Array<PurchaseInfoItem> => {
    const participationTypeLabel = formatEnum(selectedItem.participationType, {
        options: EventOptions.participationType,
        formatMessage
    });
    const participationLabel = formatMessage({ id: 'booking.purchase.participation' });

    return [
        {
            label:
                locale === 'fr-CH'
                    ? `${participationLabel} ${participationTypeLabel}`
                    : `${participationTypeLabel} ${participationLabel}`,
            price: !showIsItemAddedLabel
                ? formatPrice(selectedItem.price, locale, currencySymbol)
                : null,
            addedItem: showIsItemAddedLabel && !selectedItem.itemAlreadyBooked
        }
    ].filter(booleanFilter);
};

const getTicketDayNumber = (event: Event, item: EventItemsSelectionItem) => {
    return (
        parseDBDate(item.startDateTime)
            .startOf('day')
            .diff(parseDBDate(event.startDateTime).startOf('day'), 'days') + 1
    );
};

const getPromoCodeItem = (
    promoCode: Fragments.PromoCodeForOrderFieldsFragment,
    currencySymbol: string,
    locale: Locale
) => {
    const isPercentage = isPercentagePromoCode(promoCode);
    const promoAmountStr = formatPrice(
        -promoCode.promoAmount,
        locale,
        isPercentage ? '%' : currencySymbol
    );
    return {
        price: promoAmountStr,
        label: promoCode.name
    };
};

const getVatItem = (
    vat: number,
    currencySymbol: string,
    taxPercentage: number | null | undefined,
    formatMessage: IntlFormatters['formatMessage'],
    locale: Locale
): PurchaseInfoItem => {
    const priceStr = formatPrice(vat, locale, currencySymbol);
    return {
        price: priceStr,
        label: formatMessage({ id: 'common.vat' }, { percentage: taxPercentage })
    };
};

const getEntireEventTicketLabel = (
    ticket: EventItemsSelectionItem,
    formatMessage: IntlShape['formatMessage']
) => {
    const startDate = parseDBDate(ticket.startDateTime);
    const endDate = parseDBDate(ticket.endDateTime);

    const dateLabel = formatDateRange(startDate, endDate);

    const ticketLabel = formatMessage({ id: 'booking.event-items.complete-training' });

    return `${ticketLabel} ${dateLabel}`;
};

export const purchaseInfoFromEvent = ({
    eventForBooking,
    selectedItems,
    promoCodeApplied,
    formatMessage,
    currencySymbol,
    hideDigitalCoursebook,
    vat,
    showAddedItemsLabel,
    totalPrice,
    locale
}: {
    eventForBooking: Event;
    selectedItems: Array<EventItemsSelectionItem>;
    promoCodeApplied: Fragments.PromoCodeForOrderFieldsFragment | null;
    formatMessage: IntlFormatters['formatMessage'];
    currencySymbol: string;
    hideDigitalCoursebook: boolean;
    vat: number;
    showAddedItemsLabel: boolean;
    totalPrice: number;
    locale: Locale;
}): EventBookingPurchaseInfo => {
    const promoCodeInfo = promoCodeApplied
        ? getPromoCodeItem(promoCodeApplied, currencySymbol, locale)
        : null;
    const { taxPercentage } = eventForBooking;
    const vatInfo = vat
        ? getVatItem(vat, currencySymbol, taxPercentage, formatMessage, locale)
        : null;

    const printedCourseBookTicket = getPrintedCourseBookTicket(selectedItems);

    const updatingExistingBooking = !!eventForBooking.existingBookingId;
    const showIsItemAddedLabel = showAddedItemsLabel && updatingExistingBooking;

    const courseBooksInfo: EventPurchaseCourseBooksInfo = {
        printed: printedCourseBookTicket
            ? {
                  localeId: 'common.printed-coursebook',
                  price: !showIsItemAddedLabel
                      ? formatPrice(getTicketPrice(printedCourseBookTicket), locale, currencySymbol)
                      : null,
                  addedItem: !printedCourseBookTicket.itemAlreadyBooked && showIsItemAddedLabel
              }
            : undefined,
        digital:
            hideDigitalCoursebook || !!eventForBooking.existingBookingId
                ? undefined
                : {
                      localeId: 'common.digital-coursebook',
                      price: formatPrice(0, locale, currencySymbol)
                  }
    };

    const entireEventTicket = selectedItems.find(
        (item) => item.ticketType === Types.TicketType.EntireEvent
    );
    if (entireEventTicket) {
        const ticketsInfo = [
            [
                {
                    items: getEventPurchaseItems(
                        entireEventTicket,
                        formatMessage,
                        currencySymbol,
                        showIsItemAddedLabel,
                        locale
                    ),
                    title: getEntireEventTicketLabel(entireEventTicket, formatMessage)
                }
            ]
        ];

        return {
            ticketsInfo,
            promoCodeInfo,
            vatInfo,
            courseBooksInfo,
            event: eventForBooking,
            selectedItems,
            totalPrice,
            promoCodeApplied,
            connectedAccountId: eventForBooking.connectedAccountId || null
        };
    }

    const purchaseInfoByDay: Record<string, Array<EventBookingTicket>> = {};
    selectedItems
        .sort(
            (itemA, itemB) =>
                parseDBDate(itemA.startDateTime).date() - parseDBDate(itemB.startDateTime).date()
        )
        .filter((item) => item.ticketType !== Types.TicketType.PrintedCourseBook)
        .forEach((selectedItem) => {
            const day = getTicketDayNumber(eventForBooking, selectedItem);
            const ticketLabel = formatMessage({ id: 'booking.purchase.day' });
            const dayTitle = `${formatDate(
                selectedItem.startDateTime,
                DateFormat.dayAndMonthSingleLine
            )} (${ticketLabel} ${day})`;
            const title =
                selectedItem.ticketType === Types.TicketType.HalfDayTicket ||
                selectedItem.ticketType === Types.TicketType.OnDemand
                    ? selectedItem.name
                    : dayTitle;

            const items = getEventPurchaseItems(
                selectedItem,
                formatMessage,
                currencySymbol,
                showIsItemAddedLabel,
                locale
            );

            const sectionTitle =
                selectedItem.ticketType === Types.TicketType.HalfDayTicket
                    ? { sectionTitle: dayTitle }
                    : {};

            const ticket = {
                ...sectionTitle,
                items,
                title
            };

            const purchaseInfo = purchaseInfoByDay[day];
            if (purchaseInfo) {
                purchaseInfo.push(ticket);
            } else {
                purchaseInfoByDay[day] = [ticket];
            }
        });

    const purchaseInfo: EventBookingPurchaseInfo = {
        ticketsInfo: Object.values(purchaseInfoByDay),
        promoCodeInfo,
        vatInfo,
        courseBooksInfo,
        event: eventForBooking,
        selectedItems,
        totalPrice,
        promoCodeApplied,
        connectedAccountId: eventForBooking.connectedAccountId || null
    };

    return purchaseInfo;
};
